import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CategoryTag from "@/components/base/CategoryTag";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import { canonical, webPageSchema } from "@/utils/seo";
import { searchContent } from "@/api/content";
import type { PostCard, StoreCard } from "@/types/content";

function PostResultCard({ post }: { post: PostCard }) {
  return (
    <a
      href={`/articles/${post.slug}`}
      className="group flex gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-sm transition-all"
    >
      <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg overflow-hidden">
        <img
          loading="lazy"
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <CategoryTag label={post.category} variant="slate" />
          {(post.title.includes("TOP") || post.tags.some((t) => ["懶人包", "合集", "評比", "指南"].includes(t))) && (
            <span className="flex items-center gap-1 text-xs text-primary font-medium">
              <i className="ri-store-2-line" />
              合集
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-semibold text-slate-800 mb-1 line-clamp-2 group-hover:text-primary transition-colors">
          {post.title}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2 mb-2 hidden sm:block">
          {post.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span>{post.publishedAt}</span>
          <span>{post.readingTime} 分鐘閱讀</span>
        </div>
      </div>
    </a>
  );
}

function StoreResultCard({ store }: { store: StoreCard }) {
  return (
    <a
      href={`/stores/${store.slug}`}
      className="group flex gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-sm transition-all"
    >
      <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg overflow-hidden">
        <img
          loading="lazy"
          src={store.coverImage}
          alt={store.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <CategoryTag label={store.category} variant="slate" />
          {store.tags.includes("文化幣") && (
            <span className="px-2 py-0.5 bg-emerald-100 rounded-full text-xs font-medium text-emerald-700">
              文化幣
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-semibold text-slate-800 mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {store.name}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2 mb-2 hidden sm:block">
          {store.description}
        </p>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1 text-amber-500">
            <i className="ri-star-fill" />
            {store.rating} ({store.reviewCount})
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line" />
            {store.location}
          </span>
        </div>
      </div>
    </a>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [inputValue, setInputValue] = useState(query);
  const [results, setResults] = useState<{ posts: PostCard[]; stores: StoreCard[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    searchContent(query.trim()).then((data) => {
      setResults(data);
      setLoading(false);
    });
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    }
  };

  const totalCount = results ? results.posts.length + results.stores.length : 0;

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="max-w-4xl mx-auto">
        <SEO
          meta={{
            title: query ? `"${query}" 搜尋結果｜美好時光指南` : "搜尋｜美好時光指南",
            description: query
              ? `在美好時光指南搜尋「${query}」的相關文章與探店店家。`
              : "搜尋全站文章與探店店家，找到你想了解的評比與指南。",
            canonical: canonical(`/search${query ? `?q=${encodeURIComponent(query)}` : ""}`),
            keywords: query ? [query, "搜尋", "美好時光指南"] : ["搜尋", "文章", "探店", "美好時光指南"],
            structuredData: webPageSchema(
              query ? `"${query}" 搜尋結果｜美好時光指南` : "搜尋｜美好時光指南",
              query ? `在美好時光指南搜尋「${query}」的相關文章與探店店家。` : "搜尋全站文章與探店店家。",
              "/search"
            ),
          }}
        />

        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6">
          {query ? `"${query}" 的搜尋結果` : "搜尋全站內容"}
        </h1>

        {/* Search Input */}
        <form onSubmit={handleSubmit} className="relative mb-8">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="搜尋文章、探店店家..."
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          />
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400">
            <i className="ri-search-line text-lg" />
          </div>
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            搜尋
          </button>
        </form>

        {/* Results */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <SectionSkeleton type="spinner" minHeight="120px" className="py-0" />
          </div>
        )}

        {!loading && query && results && (
          <>
            <p className="text-sm text-slate-500 mb-6">
              找到 <span className="font-semibold text-slate-700">{totalCount}</span> 個結果
              （文章 {results.posts.length} 篇、探店店家 {results.stores.length} 家）
            </p>

            {totalCount === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-slate-100">
                  <i className="ri-search-line text-2xl text-slate-400" />
                </div>
                <p className="text-slate-500 mb-1">找不到與「{query}」相關的結果</p>
                <p className="text-sm text-slate-400">試試其他關鍵字，或瀏覽主題與城市列表</p>
              </div>
            )}

            {results.posts.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="ri-article-line text-primary" />
                  文章 ({results.posts.length})
                </h2>
                <div className="space-y-3">
                  {results.posts.map((post) => (
                    <PostResultCard key={post.id} post={post} />
                  ))}
                </div>
              </div>
            )}

            {results.stores.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="ri-store-2-line text-primary" />
                  探店店家 ({results.stores.length})
                </h2>
                <div className="space-y-3">
                  {results.stores.map((store) => (
                    <StoreResultCard key={store.id} store={store} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!query && !loading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-slate-100">
              <i className="ri-search-line text-2xl text-slate-400" />
            </div>
            <p className="text-slate-500">輸入關鍵字開始搜尋</p>
          </div>
        )}
      </div>
    </div>
  );
}